import React from 'react';

import dayjs from 'dayjs';
import { getMarketingRouterRuleMetrics } from '~/data/marketingRouterRuleMetrics';
import { Link } from '~/lib/react-router-dom';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import type { MarketingRouterRuleRecord } from '~/hooks/apis/promo/useMarketingRouterRules';

import { customerPortalHost } from '~/services/common';

import { getMarketingRouterRuleCookieName } from '~/types/marketingRouterRule';

import MarketingRouterTermLabel from './MarketingRouterTermLabel';
import { formatMatchValues, toMatchValues } from './marketingRouterMatch';
import { MARKETING_ROUTER_STATUS_STYLE, getMarketingRouterRuleStatus } from './marketingRouterRuleFilters';
import { formatTreatments } from './marketingRouterTreatmentDisplay';

/** Columns in a rules table, including the selection column. */
export const MARKETING_ROUTER_RULE_TABLE_COLUMNS = 7;

// A link can only carry one value per parameter, so it exercises the first
// value the rule lists for each field.
function firstMatchValue(values?: Array<string>): string {
  return toMatchValues(values)[0] ?? '';
}

function getCustomerPortalTestLink(
  tenant: App.Tenant,
  rule: {
    marketingRouterId: string;
    match: { utm_medium?: Array<string>; utm_source?: Array<string>; utm_campaign?: Array<string> };
  },
  variantId: string,
): string {
  // The portal keys the sticky cookie and the mrid analytics param by the
  // router id, so the test link has to carry that and not the rule's id.
  const query = new URLSearchParams({
    utm_medium: firstMatchValue(rule.match.utm_medium),
    utm_source: firstMatchValue(rule.match.utm_source),
    utm_campaign: firstMatchValue(rule.match.utm_campaign),
    mrid: rule.marketingRouterId,
    mrvid: variantId,
  });

  const portalBaseUrl = customerPortalHost(tenant)
    .replace(/\/$/, '')
    .replace(/\/[a-z]{2}$/i, '');

  return `${portalBaseUrl}?${query.toString()}`;
}

function formatDate(date: string): string {
  return dayjs(date).format('DD MMM YYYY');
}

function formatAud(value: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value);
}

function getSignificanceLabel(significance: 'pending' | 'not_significant' | 'significant'): string {
  if (significance === 'not_significant') {
    return 'Not significant';
  }

  return significance === 'significant' ? 'Significant' : 'Pending';
}

function getSignificanceColor(significance: 'pending' | 'not_significant' | 'significant') {
  if (significance === 'significant') {
    return 'success';
  }

  return significance === 'pending' ? 'warning' : 'default';
}

/** Header for a table of {@link MarketingRouterRuleTableRow}s. */
export function MarketingRouterRuleTableHead() {
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox" />
        <TableCell>
          <MarketingRouterTermLabel term="rule">Rule name</MarketingRouterTermLabel>
        </TableCell>
        <TableCell>Match</TableCell>
        <TableCell>Persistence</TableCell>
        <TableCell>Variants</TableCell>
        <TableCell>Schedule</TableCell>
        <TableCell align="right">Actions</TableCell>
      </TableRow>
    </TableHead>
  );
}

/**
 * Variant allocation for one rule. Its own component so the per-variant metrics
 * and portal links are only built while the panel is open.
 */
function VariantsPanel({ rule, tenant }: { rule: MarketingRouterRuleRecord; tenant: App.Tenant }) {
  return (
    <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Variant allocation and cookies
      </Typography>
      <Table size="small" aria-label={`Variants for rule ${rule.id}`}>
        <TableHead>
          <TableRow>
            <TableCell>Variant name</TableCell>
            <TableCell>Control</TableCell>
            <TableCell>
              <MarketingRouterTermLabel term="weight" />
            </TableCell>
            <TableCell>Treatments</TableCell>
            <TableCell>TTV</TableCell>
            <TableCell>Margin (AUD)</TableCell>
            <TableCell>Sessions</TableCell>
            <TableCell>Significance</TableCell>
            <TableCell>Cookie name</TableCell>
            <TableCell>Customer portal</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rule.variants.map((variant) => {
            const metrics = getMarketingRouterRuleMetrics(rule.id, variant.variant_id);

            return (
              <TableRow key={variant.variant_id}>
                <TableCell>{variant.variant_name}</TableCell>
                <TableCell>{variant.is_control ? 'Control' : 'Test variant'}</TableCell>
                <TableCell>{variant.weight}%</TableCell>
                <TableCell>{formatTreatments(variant.treatments)}</TableCell>
                <TableCell>{formatAud(metrics.ttv)}</TableCell>
                <TableCell>{formatAud(metrics.margin_aud)}</TableCell>
                <TableCell>{metrics.sessions.toLocaleString('en-AU')}</TableCell>
                <TableCell>
                  <Chip
                    label={getSignificanceLabel(metrics.significance)}
                    color={getSignificanceColor(metrics.significance)}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>
                  {getMarketingRouterRuleCookieName(rule.marketingRouterId, variant.variant_id)}
                </TableCell>
                <TableCell>
                  <MuiLink
                    href={getCustomerPortalTestLink(tenant, rule, variant.variant_id)}
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                  >
                    test link
                  </MuiLink>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

type Props = {
  rule: MarketingRouterRuleRecord;
  tenant: App.Tenant;
  /** Today as a Sydney calendar day, which rule dates are compared against. */
  today: string;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  /** Comparison selection. Without it the selection cell stays empty so columns still line up. */
  selection?: {
    isSelected: boolean;
    disabled: boolean;
    onToggle: () => void;
  };
};

/**
 * One rule in a rules table, with its variants in a panel that expands below
 * it. Shared by the Rules tab and the URL resolver so a matched rule reads
 * exactly as it does in the list.
 */
export default function MarketingRouterRuleTableRow({
  rule,
  tenant,
  today,
  isExpanded,
  onToggleExpanded,
  selection,
}: Props) {
  const variantsTotal = rule.variants.reduce((total, variant) => total + variant.weight, 0);
  const status = getMarketingRouterRuleStatus(rule, today);
  const resultsPath = `/marketing/marketing-router-rules/${encodeURIComponent(rule.marketingRouterId)}`;

  return (
    <>
      <TableRow hover selected={selection?.isSelected}>
        <TableCell padding="checkbox">
          {selection ? (
            <Checkbox
              checked={selection.isSelected}
              disabled={selection.disabled}
              onChange={selection.onToggle}
              inputProps={{ 'aria-label': `Select rule ${rule.name} to compare` }}
            />
          ) : null}
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>
          <MuiLink component={Link} to={resultsPath} underline="hover">
            {rule.name}
          </MuiLink>
        </TableCell>
        <TableCell>
          <Stack spacing={0.25}>
            {Object.entries(rule.match).map(([field, value]) => (
              <Typography key={field} variant="body2">
                <Typography component="span" variant="body2" color="text.secondary">
                  {field.replace('utm_', 'UTM ')}:{' '}
                </Typography>
                {formatMatchValues(value)}
              </Typography>
            ))}
          </Stack>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{rule.variants.length} cookies</Typography>
          <Typography variant="caption" color="text.secondary">
            One per variant
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {rule.variants.length} {rule.variants.length === 1 ? 'variant' : 'variants'}
          </Typography>
          <Typography variant="caption" color={variantsTotal === 100 ? 'text.secondary' : 'error'}>
            {variantsTotal}% allocated
          </Typography>
        </TableCell>
        <TableCell>
          <Stack spacing={0.5} alignItems="flex-start">
            <Chip label={status} size="small" color={MARKETING_ROUTER_STATUS_STYLE[status].chipColor} />
            <Typography variant="body2">{formatDate(rule.start_date)}</Typography>
            <Typography variant="caption" color="text.secondary">
              to {formatDate(rule.end_date)}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell align="right">
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
            <IconButton
              size="small"
              aria-label={`${isExpanded ? 'Hide' : 'View'} variants for rule ${rule.id}`}
              aria-expanded={isExpanded}
              onClick={onToggleExpanded}
            >
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
            <Button component={Link} to={resultsPath} variant="text">
              Results
            </Button>
            <Button component={Link} to={`${resultsPath}/edit`} variant="text">
              Edit
            </Button>
            <Button
              component={Link}
              to={`/marketing/marketing-router-rules/create?duplicateFrom=${encodeURIComponent(rule.marketingRouterId)}`}
              variant="text"
              startIcon={<ContentCopyIcon fontSize="small" />}
              aria-label={`Duplicate rule ${rule.name}`}
            >
              Duplicate
            </Button>
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={MARKETING_ROUTER_RULE_TABLE_COLUMNS}
          sx={{ p: 0, borderBottom: isExpanded ? undefined : 0 }}
        >
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <VariantsPanel rule={rule} tenant={tenant} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
